import { useState, useEffect } from 'react'
import { Trophy, TrendingUp, TrendingDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { formatCurrency, formatPercent, cn } from '@/lib/utils'
import type { LeaderboardEntry } from '@/types'

const MEDALS = ['🥇', '🥈', '🥉']

export default function Leaderboard() {
  const { user } = useAuth()
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const load = async () => {
    setIsLoading(true)
    const res = await api.getLeaderboard(page, 25)
    if (res.success && res.data) {
      setEntries(res.data)
      setTotalPages(res.meta?.totalPages ?? 1)
    }
    setIsLoading(false)
  }

  useEffect(() => { load() }, [page])

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">Leaderboard</h1>
        <p className="text-white/40 text-sm mt-0.5">Top performers ranked by portfolio value</p>
      </div>

      {/* Top 3 podium */}
      {!isLoading && entries.length >= 3 && page === 1 && (
        <div className="grid grid-cols-3 gap-4 mb-2">
          {[1, 0, 2].map((i) => {
            const entry = entries[i]
            if (!entry) return null
            const rank = i + 1
            const isMe = entry.user_id === user?.id
            const isPositive = entry.total_return_pct >= 0

            return (
              <div
                key={entry.user_id}
                className={cn(
                  'card p-5 text-center relative',
                  i === 0 ? 'ring-1 ring-yellow-500/30 bg-yellow-500/[0.04]' : '',
                  isMe ? 'ring-1 ring-accent/30' : ''
                )}
              >
                {isMe && (
                  <div className="absolute top-2 right-2 text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded-full font-medium">
                    You
                  </div>
                )}
                <div className="text-2xl mb-2">{MEDALS[i]}</div>
                <div className="font-semibold text-sm">{entry.username}</div>
                <div className="text-lg font-mono font-bold mt-1">
                  {formatCurrency(entry.total_portfolio_value)}
                </div>
                <div className={cn('text-xs mt-0.5 flex items-center justify-center gap-0.5', isPositive ? 'text-gain' : 'text-loss')}>
                  {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {formatPercent(entry.total_return_pct)}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Full table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-14 bg-white/[0.04] rounded-xl animate-pulse" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center">
            <Trophy className="w-10 h-10 text-white/15 mx-auto mb-3" />
            <div className="text-white/30 text-sm">No traders yet. Be the first!</div>
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead className="border-b border-white/[0.06]">
                <tr>
                  {['Rank', 'Trader', 'Portfolio Value', 'Return'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-white/30 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {entries.map((entry, idx) => {
                  const rank = (page - 1) * 25 + idx + 1
                  const isMe = entry.user_id === user?.id
                  const isPositive = entry.total_return_pct >= 0

                  return (
                    <tr
                      key={entry.user_id}
                      className={cn(
                        'transition-colors',
                        isMe ? 'bg-accent/[0.04] hover:bg-accent/[0.06]' : 'hover:bg-white/[0.02]'
                      )}
                    >
                      <td className="px-4 py-4 w-14">
                        <div className="flex items-center gap-1">
                          {rank <= 3 ? (
                            <span className="text-base">{MEDALS[rank - 1]}</span>
                          ) : (
                            <span className="text-sm text-white/30 font-mono w-6 text-center">
                              {rank}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-white/[0.06] rounded-full flex items-center justify-center text-xs font-bold text-white/50">
                            {entry.username[0].toUpperCase()}
                          </div>
                          <span className={cn('text-sm font-medium', isMe ? 'text-accent' : '')}>
                            {entry.username}
                          </span>
                          {isMe && (
                            <span className="text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded-full font-medium">
                              You
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 font-mono text-sm font-medium">
                        {formatCurrency(entry.total_portfolio_value)}
                      </td>
                      <td className={cn('px-4 py-4 text-sm', isPositive ? 'text-gain' : 'text-loss')}>
                        <span className="flex items-center gap-1 font-mono">
                          {isPositive
                            ? <TrendingUp className="w-3.5 h-3.5" />
                            : <TrendingDown className="w-3.5 h-3.5" />
                          }
                          {formatPercent(entry.total_return_pct)}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1 disabled:opacity-30"
                >
                  <ChevronLeft className="w-3.5 h-3.5" /> Prev
                </button>
                <span className="text-xs text-white/30">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1 disabled:opacity-30"
                >
                  Next <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
